import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { NavLink, Outlet as ComponentOfCurrentRoute, useNavigate } from 'react-router'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import CustomerIcon from '@mui/icons-material/Business'
import LicenseIcon from '@mui/icons-material/VpnKey'
import BillingIcon from '@mui/icons-material/Payment'
import Person from '@mui/icons-material/Person'
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop'
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined'
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import HistoryIcon from '@mui/icons-material/History'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'

import { SearchPaletteProvider, SearchPaletteSidebarItem } from 'common-ui/components/SearchPaletteProvider'
import { SidebarLayout } from 'common-ui/components/SidebarLayout'
import { type SidebarMenuItemProps } from 'common-ui/components/SidebarMenuItem'
import nimbraBillingLogo from 'common-ui/img/logo-billing.svg'
import { INITIAL_PATH, Path } from '../routes/path'
import { useAuth } from './login/Auth'
import { SidebarProvider, useSidebar } from './common/SidebarContext'
import { BuildInfo, Role, User, UserManagementSystem } from 'common-billing-server/types'
import { isRoleAuthorized } from 'common-billing-server/role'
import { useBillingApi } from '../api/billing/billing-context'
import { mixpanel } from '../mixpanel'
import { useBillingSearchSources } from './common/globalSearch'

interface MenuItem {
    name: string
    icon: React.ReactElement
    url?: Path
    children?: SidebarMenuItemProps[]
    minimumAllowedRole?: Role
    divider?: boolean
}

const allMenuItems: MenuItem[] = [
    { name: 'Customers', icon: <CustomerIcon />, url: Path.customers, minimumAllowedRole: Role.licenseAdmin },
    { name: 'Licenses', icon: <LicenseIcon />, url: Path.licenses, minimumAllowedRole: Role.licenseAdmin },
    { name: 'Installations', icon: <InstallDesktopIcon />, url: Path.installations },
    { name: 'Managed hosting', icon: <CloudOutlinedIcon />, url: Path.managedHosting, minimumAllowedRole: Role.super },
    { name: 'Appliances', icon: <DnsOutlinedIcon />, url: Path.appliances },
    { name: 'Sales', icon: <TrendingUpIcon />, url: Path.sales, minimumAllowedRole: Role.super },
    { name: 'Billing', icon: <BillingIcon />, url: Path.billing2 },
    { name: 'Billing (legacy)', icon: <BillingIcon />, url: Path.billing },
    {
        name: 'AI',
        icon: <AutoAwesomeIcon />,
        minimumAllowedRole: Role.super,
        children: [
            { name: 'Logs', url: Path.ai },
            { name: 'Settings', url: Path.aiSettings },
        ],
    },
    {
        name: 'Users',
        icon: <Person />,
        minimumAllowedRole: Role.licenseAdmin,
        divider: true,
        children: [
            { name: 'Internal', url: Path.internalUsers },
            { name: 'External', url: Path.externalUsers },
        ],
    },
    { name: 'Audit log', icon: <HistoryIcon />, url: Path.auditLog, minimumAllowedRole: Role.admin },
    { name: 'Global settings', icon: <SettingsIcon />, url: Path.settings, minimumAllowedRole: Role.super },
]

function filterMenuItems(role: Role): SidebarMenuItemProps[] {
    return allMenuItems
        .filter(({ minimumAllowedRole }) => !minimumAllowedRole || isRoleAuthorized({ role, minimumAllowedRole }))
        .map(({ name, icon, url, children, divider }) => ({ name, icon, url, children, divider }))
}

function profileUrlFor(user: User | undefined): Path | undefined {
    const canManageSshKeys = isRoleAuthorized({ role: user?.role ?? Role.invoiceOnly, minimumAllowedRole: Role.admin })
    return user?.managedBy === UserManagementSystem.billingServer || canManageSshKeys ? Path.profile : undefined
}

export const AppLayout = () => {
    return (
        <SidebarProvider>
            <BillingSearch>
                <AppLayoutContent />
            </BillingSearch>
        </SidebarProvider>
    )
}

/** ⌘K search over the pages, actions and entities the signed-in role can open. */
const BillingSearch = ({ children }: { children: ReactNode }) => {
    const { user } = useAuth()
    const role = user?.role ?? Role.invoiceOnly
    const menuItems = useMemo(() => filterMenuItems(role), [role])
    const { localSources, remoteSources } = useBillingSearchSources(role, menuItems, profileUrlFor(user))

    return (
        <SearchPaletteProvider
            localSources={localSources}
            remoteSources={remoteSources}
            label="Search Nimbra Billing"
            emptyHint="Search for pages, customers, licenses, installations, appliances and users"
            onSelect={({ target }) => {
                if (target.kind === 'navigate') mixpanel.trackClickLink({ component: 'GlobalSearch', to: target.to })
            }}
        >
            {children}
        </SearchPaletteProvider>
    )
}

const AppLayoutContent = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const api = useBillingApi()
    const { open, onOpen, onClose } = useSidebar()
    const [buildInfo, setBuildInfo] = useState<BuildInfo | undefined>(undefined)

    useEffect(() => {
        void api.getBuildInfo().then((info) => {
            setBuildInfo(info)
        })
    }, [api])

    const userRole = user?.role ?? Role.invoiceOnly
    const profileUrl = profileUrlFor(user)

    return (
        <Box sx={{ height: '100vh', width: '100vw', display: 'flex' }}>
            <SidebarLayout
                open={open}
                onOpen={onOpen}
                onClose={onClose}
                header={
                    <NavLink
                        to={INITIAL_PATH}
                        style={{ display: 'block', marginBottom: '-1px', textDecoration: 'none', color: 'inherit' }}
                    >
                        <Box
                            sx={(theme) => ({
                                ...theme.mixins.toolbar,
                                minHeight: 52,
                                '@media (orientation: landscape) and (max-width: 599.95px)': {
                                    minHeight: 44,
                                },
                                [theme.breakpoints.up('sm')]: {
                                    minHeight: 60,
                                },
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                            })}
                        >
                            <img
                                src={nimbraBillingLogo}
                                alt="Nimbra Billing"
                                style={{ width: '100%', display: 'block' }}
                            />
                        </Box>
                    </NavLink>
                }
                headerExtra={<SearchPaletteSidebarItem />}
                menuItems={filterMenuItems(userRole)}
                user={{
                    name: user?.username ?? 'Unknown user',
                    profileUrl,
                    onLogout: () => navigate(Path.logout),
                }}
                version={
                    <Typography variant="caption" style={{ padding: '0 16px 8px' }}>
                        {buildInfo?.release || ''}
                    </Typography>
                }
            />
            <Box component="main" sx={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
                <ComponentOfCurrentRoute />
            </Box>
        </Box>
    )
}
